/**
 * Animation Utilities
 *
 * Reusable Framer Motion animation variants and hooks for consistent
 * micro-interactions across the Nour Al-Arabia platform.
 *
 * Design Philosophy:
 * - Premium, refined motion that feels intentional
 * - Spring physics for organic, natural feel
 * - RTL-aware directional animations
 * - Respects prefers-reduced-motion for accessibility
 * - GPU-accelerated properties only (x, y, scale, opacity, rotate)
 */

import { useEffect, useState, useMemo } from 'react';
import type { Variants, Transition } from 'framer-motion';

// =============================================================================
// Reduced Motion Hook
// =============================================================================

/**
 * Hook to detect user's motion preference
 *
 * Returns true if the user has requested reduced motion in their OS settings.
 * Use this to disable or simplify animations for accessibility.
 *
 * @example
 * ```tsx
 * const prefersReducedMotion = useReducedMotion();
 * const variants = prefersReducedMotion ? reducedVariants : fullVariants;
 * ```
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(
    () => {
      // Check on initial render (SSR safe)
      if (typeof window === 'undefined') return false;
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion;
}

// =============================================================================
// Directional Slide Hook
// =============================================================================

/**
 * Direction-aware slide values for RTL support
 */
export interface DirectionalSlide {
  /** Slide distance in pixels (positive = away from start, negative = toward start) */
  slideX: number;
  /** Inverted slide distance */
  slideXInverse: number;
  /** Direction flag */
  isRTL: boolean;
}

/**
 * Hook for RTL-aware slide animations
 *
 * Provides slide values that automatically flip for RTL layouts.
 * Use with Framer Motion's x property for consistent directional motion.
 *
 * @param distance - The slide distance in pixels (default: 20)
 * @param isRTL - Whether the layout is RTL (usually from useDirection hook)
 *
 * @example
 * ```tsx
 * const { isRTL } = useDirection();
 * const { slideX } = useDirectionalSlide(isRTL, 30);
 *
 * const variants = {
 *   hidden: { opacity: 0, x: slideX },
 *   visible: { opacity: 1, x: 0 },
 * };
 * ```
 */
export function useDirectionalSlide(
  isRTL: boolean,
  distance: number = 20
): DirectionalSlide {
  return useMemo(
    () => ({
      slideX: isRTL ? distance : -distance,
      slideXInverse: isRTL ? -distance : distance,
      isRTL,
    }),
    [isRTL, distance]
  );
}

// =============================================================================
// Spring Presets
// =============================================================================

/**
 * Spring physics presets for different animation contexts
 *
 * - snappy: Quick, responsive interactions (buttons, toggles)
 * - gentle: Smooth, flowing motion (page transitions, large elements)
 * - bouncy: Playful, attention-grabbing (success states, notifications)
 * - stiff: Precise, controlled motion (tool tips, menus)
 */
export const springPresets = {
  snappy: { type: 'spring', stiffness: 400, damping: 30 } as Transition,
  gentle: { type: 'spring', stiffness: 100, damping: 20 } as Transition,
  bouncy: { type: 'spring', stiffness: 300, damping: 15 } as Transition,
  stiff: { type: 'spring', stiffness: 500, damping: 40 } as Transition,
} as const;

// =============================================================================
// Base Variants
// =============================================================================

/**
 * Fade in with upward motion
 *
 * Classic entrance animation. Element fades in while sliding up.
 * Great for: Cards, list items, content sections
 */
export const fadeInUp: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: springPresets.gentle,
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2 },
  },
};

/**
 * Fade in with downward motion
 *
 * For elements that drop in from above.
 * Great for: Dropdowns, tooltips, notifications
 */
export const fadeInDown: Variants = {
  hidden: {
    opacity: 0,
    y: -20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: springPresets.gentle,
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2 },
  },
};

/**
 * Scale in from slightly smaller
 *
 * Elegant entrance that grows into place.
 * Great for: Modals, dialogs, popovers, focus elements
 */
export const scaleIn: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springPresets.snappy,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 },
  },
};

/**
 * Scale in with more dramatic effect
 *
 * Stronger scale for attention-grabbing entrances.
 * Great for: Celebration modals, success states
 */
export const scaleInBouncy: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springPresets.bouncy,
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: 0.2 },
  },
};

/**
 * Slide in from start (left in LTR, right in RTL)
 *
 * Note: For RTL-aware animations, use useDirectionalSlide hook
 * and create variants dynamically.
 */
export const slideInStart: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: springPresets.gentle,
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.2 },
  },
};

/**
 * Slide in from end (right in LTR, left in RTL)
 *
 * Note: For RTL-aware animations, use useDirectionalSlide hook
 * and create variants dynamically.
 */
export const slideInEnd: Variants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: springPresets.gentle,
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: { duration: 0.2 },
  },
};

// =============================================================================
// Container Variants (for stagger effects)
// =============================================================================

/**
 * Container for staggered children animations
 *
 * Apply to parent element, children should use item variants.
 * Staggers children with 0.06s delay for smooth cascade effect.
 *
 * @example
 * ```tsx
 * <motion.ul variants={staggerContainer} initial="hidden" animate="visible">
 *   {items.map(item => (
 *     <motion.li key={item.id} variants={staggerItem}>
 *       {item.content}
 *     </motion.li>
 *   ))}
 * </motion.ul>
 * ```
 */
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1,
    },
  },
};

/**
 * Fast stagger container for quick lists
 *
 * Shorter delays for smaller, simpler items.
 */
export const staggerContainerFast: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.05,
    },
  },
};

/**
 * Slow stagger container for larger items
 *
 * Longer delays for complex cards or large content blocks.
 */
export const staggerContainerSlow: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.15,
    },
  },
};

/**
 * Default stagger item
 *
 * Fades up with spring physics. Pair with staggerContainer.
 */
export const staggerItem: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15,
    },
  },
};

/**
 * Scale-based stagger item
 *
 * Scales up instead of sliding. Good for grid layouts.
 */
export const staggerItemScale: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 18,
    },
  },
};

// =============================================================================
// Hover & Tap Animations
// =============================================================================

/**
 * Subtle hover scale for interactive elements
 *
 * Apply to motion elements that are clickable.
 * Provides gentle feedback without being distracting.
 */
export const hoverScale = {
  whileHover: { scale: 1.02 },
  whileTap: { scale: 0.98 },
  transition: springPresets.snappy,
};

/**
 * More prominent hover scale for cards
 *
 * Slightly larger scale for bigger interactive areas.
 */
export const hoverScaleCard = {
  whileHover: { scale: 1.03, y: -4 },
  whileTap: { scale: 0.98 },
  transition: springPresets.snappy,
};

/**
 * Button press feedback
 *
 * Quick, responsive tap animation for buttons.
 */
export const tapFeedback = {
  whileTap: { scale: 0.96 },
  transition: springPresets.stiff,
};

// =============================================================================
// Special Effects
// =============================================================================

/**
 * Shake animation for errors/validation
 *
 * Use with animate prop triggered by error state.
 *
 * @example
 * ```tsx
 * <motion.div
 *   animate={hasError ? shakeAnimation : {}}
 * >
 *   Form content
 * </motion.div>
 * ```
 */
export const shakeAnimation = {
  x: [0, -10, 10, -10, 10, 0],
  transition: { duration: 0.5 },
};

/**
 * Attention pulse for drawing focus
 *
 * Subtle scale pulse for important elements.
 */
export const pulseAnimation = {
  scale: [1, 1.05, 1],
  transition: {
    duration: 0.6,
    repeat: 2,
  },
};

// =============================================================================
// Page Transition Variants
// =============================================================================

/**
 * Page fade transition
 *
 * Simple, clean page transitions.
 */
export const pageTransition: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

/**
 * Page slide transition
 *
 * Page slides in from the right.
 * For RTL, create dynamically with useDirectionalSlide.
 */
export const pageSlideTransition: Variants = {
  initial: {
    opacity: 0,
    x: 20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: springPresets.gentle,
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.2 },
  },
};

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Creates RTL-aware slide variants
 *
 * @param isRTL - Whether the layout is RTL
 * @param distance - Slide distance in pixels
 * @param direction - 'start' (left in LTR) or 'end' (right in LTR)
 */
export function createDirectionalSlideVariants(
  isRTL: boolean,
  distance: number = 20,
  direction: 'start' | 'end' = 'start'
): Variants {
  // Calculate the actual X offset based on direction and RTL
  let offset: number;
  if (direction === 'start') {
    offset = isRTL ? distance : -distance;
  } else {
    offset = isRTL ? -distance : distance;
  }

  return {
    hidden: {
      opacity: 0,
      x: offset,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: springPresets.gentle,
    },
    exit: {
      opacity: 0,
      x: offset / 2,
      transition: { duration: 0.2 },
    },
  };
}

/**
 * Creates reduced motion variants
 *
 * Returns simplified variants for users who prefer reduced motion.
 * Only uses opacity, no transforms.
 */
export function createReducedMotionVariants(): Variants {
  return {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.15 },
    },
  };
}

/**
 * Hook that returns appropriate variants based on motion preference
 *
 * @param fullVariants - Variants to use with full motion
 * @param reducedVariants - Optional simplified variants (defaults to fade only)
 */
export function useMotionVariants(
  fullVariants: Variants,
  reducedVariants?: Variants
): Variants {
  const prefersReducedMotion = useReducedMotion();

  return useMemo(() => {
    if (prefersReducedMotion) {
      return reducedVariants ?? createReducedMotionVariants();
    }
    return fullVariants;
  }, [prefersReducedMotion, fullVariants, reducedVariants]);
}

// =============================================================================
// Exports
// =============================================================================

export default {
  // Hooks
  useReducedMotion,
  useDirectionalSlide,
  useMotionVariants,

  // Presets
  springPresets,

  // Base variants
  fadeInUp,
  fadeInDown,
  scaleIn,
  scaleInBouncy,
  slideInStart,
  slideInEnd,

  // Container variants
  staggerContainer,
  staggerContainerFast,
  staggerContainerSlow,
  staggerItem,
  staggerItemScale,

  // Hover/Tap
  hoverScale,
  hoverScaleCard,
  tapFeedback,

  // Effects
  shakeAnimation,
  pulseAnimation,

  // Page transitions
  pageTransition,
  pageSlideTransition,

  // Utilities
  createDirectionalSlideVariants,
  createReducedMotionVariants,
};
